// The blocks of related records on a single (R-U20, um/2026-10-31/01 п.5): each relation a block, shown in full — the list of records —
// or briefly — a heading line with the count and chips. The order and the brief ones are a setting of the section kept on the server
// (TypeDef.blocks): the owner drags a block by the handle on its heading and switches its view in the single itself.

import { recordTitle, type FieldDef, type Id, type RecordObj } from '@confdeck/core-shared';
import { useState } from 'react';
import { PopoverEditor } from '../fields/FieldEditor.tsx';
import { setField } from '../sync/mutations.ts';
import { useLinked, useMeta, useSync, useTypesTick } from '../sync/react.tsx';
import { Icon } from '../ui/icons.tsx';
import { Button, IconButton, Popover, PopoverAnchor, PopoverContent, RecordChip, cx } from '../ui/primitives.tsx';
import { useSortable } from '../ui/sortable.ts';
import { nextBlocks, orderBlocks } from './blocks.ts';
import { useWritable } from './Prop.tsx';
import s from './record.module.css';

/** Chips of a brief block: the rest is the count after them. */
const CHIPS = 8;

export function RelationBlocks({ rec, fields, onOpen }: { rec: RecordObj; fields: FieldDef[]; onOpen: (id: Id) => void }) {
  const client = useSync();
  const meta = useMeta();
  const type = meta.type(rec.typeId);
  // The blocks are the section's structure: only the owner arranges them, as the fields (R-C8; the server checks it again).
  const arranges = meta.user(client.header?.userId)?.role === 'owner';
  const blocks = type?.blocks;
  const ordered = orderBlocks(fields, blocks?.order);
  const brief = new Set(blocks?.brief ?? []);
  const save = (patch: Parameters<typeof nextBlocks>[1]) => {
    if (!type) return;
    const next = nextBlocks(blocks, patch);
    client.mutate('type.update', { id: type.id, blocks: next }, [{ kind: 'type', id: type.id, op: 'patch', data: { blocks: next } }]);
  };
  const sort = useSortable(ordered.map((f) => f.key), (order) => save({ order }));
  return (
    <div className={s.blocks}>
      {ordered.map((f) => (
        <RelationBlock key={f.id} rec={rec} field={f} onOpen={onOpen} brief={brief.has(f.key)} blockRef={sort.ref(f.key)} style={sort.style(f.key)} dragging={sort.dragging === f.key}
          arrange={arranges ? {
            handle: sort.handle(f.key),
            onBrief: () => save({ order: ordered.map((x) => x.key), brief: brief.has(f.key) ? [...brief].filter((k) => k !== f.key) : [...brief, f.key] }),
          } : undefined} />
      ))}
    </div>
  );
}

function RelationBlock({ rec, field, onOpen, brief, arrange, blockRef, style, dragging }: {
  rec: RecordObj; field: FieldDef; onOpen: (id: Id) => void; brief: boolean;
  arrange?: { handle: ReturnType<ReturnType<typeof useSortable>['handle']>; onBrief: () => void };
  blockRef: (el: HTMLElement | null) => void; style?: React.CSSProperties; dragging: boolean;
}) {
  const meta = useMeta();
  const writable = useWritable(rec);
  const client = useSync();
  const ids = useLinked(rec, field.key);
  const [open, setOpen] = useState(false);
  const target = meta.relationTarget(field);
  const ttype = meta.type(target?.typeId);
  useTypesTick(ttype ? [ttype.id] : []);
  // «N из M» (stage D, tasks п. 8): when the linked records move through a pipeline with finals, done ones are counted;
  // cancelled ones (the «lost» final) leave the count.
  const stage = ttype ? meta.fields(ttype.id).find((f) => f.fieldType === 'taxonomy' && f.required && meta.taxonomy(f.options.taxonomyId)?.style === 'pipeline') : undefined;
  const progress = stage && ids.length ? (() => {
    let done = 0, total = 0;
    for (const rid of ids) { const fin = meta.term(String(client.store.record(rid)?.data[stage.key] ?? ''))?.final; if (fin === 'lost') continue; total++; if (fin === 'won') done++; }
    return { done, total };
  })() : null;
  const title = (rid: Id) => recordTitle(client.store.record(rid), meta);
  return (
    <section ref={blockRef} style={style} className={cx(s.block, brief && s.blockBrief, dragging && s.blockDragging)} aria-label={field.name}>
      <h2 className={s.blockTitle}>
        {arrange && (
          <span className={s.blockGutter}>
            <button type="button" className={s.blockHandle} aria-label={`Переместить блок «${field.name}» (стрелки вверх и вниз)`} title="Перетащите, чтобы переместить" {...arrange.handle}><Icon name="grip" size={14} /></button>
          </span>
        )}
        <span className={s.blockName}>{field.name}</span>
        <span className={s.count}>{progress ? `${progress.done} из ${progress.total}` : ids.length || ''}</span>
        {progress && progress.total > 0 && <span className={s.progress} aria-label={`Выполнено ${progress.done} из ${progress.total}`}><span style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }} /></span>}
        {brief && ids.length > 0 && (
          <span className={s.briefChips}>
            {ids.slice(0, CHIPS).map((rid) => <RecordChip key={rid} icon={ttype?.icon ?? 'square'} onClick={() => onOpen(rid)}>{title(rid)}</RecordChip>)}
            {ids.length > CHIPS && <span className={s.count}>+{ids.length - CHIPS}</span>}
          </span>
        )}
        {brief && !ids.length && <span className={s.briefEmpty}>нет</span>}
        <span className={s.blockTools}>
          {arrange && <IconButton size="sm" icon={brief ? 'list' : 'collapse'} className={s.blockView} label={brief ? 'Показывать подробно' : 'Показывать кратко'} hint={`${brief ? 'Подробно' : 'Кратко'} — для всех записей раздела`} onClick={arrange.onBrief} />}
          <Popover open={open} onOpenChange={setOpen}>
            {!field.options.managed && writable && <PopoverAnchor asChild><Button size="sm" icon="plus" onClick={() => setOpen(true)}>{target?.many ? 'Добавить' : ids.length ? 'Изменить' : 'Выбрать'}</Button></PopoverAnchor>}
            <PopoverContent width={300} align="end"><PopoverEditor rec={rec} field={field} onDone={() => setOpen(false)} /></PopoverContent>
          </Popover>
        </span>
      </h2>
      {!brief && (ids.length ? (
        <ul className={s.related}>
          {ids.slice(0, 50).map((rid) => (
            <li key={rid} className={s.relItem} onClick={() => onOpen(rid)}>
              <Icon name={ttype?.icon ?? 'square'} size={14} />
              <span className={s.relTitle}>{title(rid)}</span>
              {!field.options.managed && writable && <IconButton size="sm" icon="close" label="Отвязать" className={s.relRemove} onClick={(e) => { e.stopPropagation(); setField(client, rec, field.key, ids.filter((x) => x !== rid)); }} />}
            </li>
          ))}
          {ids.length > 50 && <li className={s.empty}>и ещё {ids.length - 50}</li>}
        </ul>
      ) : <div className={s.empty}>Нет связанных записей</div>)}
    </section>
  );
}
